import React from 'react'
import { CodeTabs, CodeBlock } from './CodeTabs'
import { Endpoint } from './Endpoint'
import { simulatedExamples } from './simulatedExamples'

/**
 * Very small Markdown-ish renderer, dependency-free (keeps CRA 1.x build untouched).
 * Supports only what the docs pages need: headings, paragraphs, bold/italic/inline code,
 * links, lists, blockquotes, tables, horizontal rules, fenced code blocks, a custom
 * ```tabs fence for multi-language code samples (```tabs-auth-login and
 * ```tabs-auth-refresh add local authentication demos; ```tabs-price adds a price demo;
 * ```tabs-<simulation ID> enables the matching fixture in simulatedExamples.js)
 * and a ```endpoint fence for a colored
 * HTTP method + URL badge (see CodeTabs.js / Endpoint.js).
 *
 * Content authored in ./content/*.js is trusted (written by developers). Plain text is
 * rendered as normal JSX text (React escapes it automatically); only the inner text of
 * bold/italic/code/link tokens goes through dangerouslySetInnerHTML, and is HTML-escaped
 * first so no raw HTML/script can leak in even there.
 */

function escapeHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

// Only allow safe link targets (no javascript: URIs, etc).
function isSafeHref(href) {
  return /^(https?:\/\/|\/|#)/i.test(href)
}

function renderInline(text, keyPrefix) {
  const listItems = text.split(/<br\s*\/?>/i)
  if (listItems.length > 1 && listItems.every(item => /^\s*-\s+/.test(item))) {
    return (
      <ul className="docs-list" key={keyPrefix}>
        {listItems.map((item, index) => (
          <li key={index}>{renderInline(item.replace(/^\s*-\s+/, ''), keyPrefix + '-' + index)}</li>
        ))}
      </ul>
    )
  }
  const nodes = []
  // Order matters: inline code, then bold, then italic, then links.
  const tokenRegex = /(<br\s*\/?>)|(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(\[[^\]]+\]\([^)]+\))/gi
  let lastIndex = 0
  let match
  let index = 0

  while ((match = tokenRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.substring(lastIndex, match.index))
    }
    const token = match[0]
    const key = keyPrefix + '-' + (index++)

    if (/^<br\s*\/?>$/i.test(token)) {
      nodes.push(<br key={key} />)
    } else if (token.startsWith('`')) {
      nodes.push(<code className="docs-inline-code" key={key} dangerouslySetInnerHTML={{ __html: escapeHtml(token.slice(1, -1)) }} />)
    } else if (token.startsWith('**')) {
      nodes.push(<strong key={key} dangerouslySetInnerHTML={{ __html: escapeHtml(token.slice(2, -2)) }} />)
    } else if (token.startsWith('*')) {
      nodes.push(<em key={key} dangerouslySetInnerHTML={{ __html: escapeHtml(token.slice(1, -1)) }} />)
    } else {
      const linkMatch = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token)
      const label = linkMatch[1]
      const href = linkMatch[2]
      if (isSafeHref(href)) {
        nodes.push(<a key={key} href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noreferrer" dangerouslySetInnerHTML={{ __html: escapeHtml(label) }} />)
      } else {
        nodes.push(label)
      }
    }
    lastIndex = tokenRegex.lastIndex
  }
  if (lastIndex < text.length) nodes.push(text.substring(lastIndex))

  return nodes
}

function slugifyHeading(text) {
  return text
    .replace(/[`*_()[\]{}]/g, '')
    .replace(/&/g, 'and')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function Markdown({ source }) {
  const lines = (source || '').replace(/\r\n/g, '\n').split('\n')
  const blocks = []
  const headingIds = new Set()
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    // Fenced code blocks, including the custom ```tabs variant.
    const fenceMatch = /^```(\S*)\s*$/.exec(line)
    if (fenceMatch) {
      const lang = fenceMatch[1]
      const codeLines = []
      i++
      while (i < lines.length && !/^```\s*$/.test(lines[i])) {
        codeLines.push(lines[i])
        i++
      }
      i++ // skip closing fence
      const codeText = codeLines.join('\n')
      const simulation = { 'tabs-auth-login': 'login', 'tabs-auth-refresh': 'refresh' }[lang] ||
        (lang.startsWith('tabs-') ? lang.slice(5) : null)
      if (lang === 'tabs' || Object.prototype.hasOwnProperty.call(simulatedExamples, simulation)) {
        blocks.push(<CodeTabs key={lang + '-' + blocks.length} source={codeText} simulation={simulation} />)
      } else if (lang === 'endpoint') {
        blocks.push(<Endpoint key={blocks.length} source={codeText} />)
      } else {
        blocks.push(<CodeBlock key={blocks.length} language={lang} code={codeText} />)
      }
      continue
    }

    if (line.trim() === '') { i++; continue }

    // Horizontal rule.
    if (/^-{3,}$/.test(line.trim())) {
      blocks.push(<hr key={blocks.length} className="docs-hr" />)
      i++
      continue
    }

    // Headings.
    const headingMatch = /^(#{1,4})\s+(.*)$/.exec(line)
    if (headingMatch) {
      const level = headingMatch[1].length
      const Tag = 'h' + Math.min(level + 1, 6)
      const explicitIdMatch = /\s+\{#([a-z0-9-]+)\}$/.exec(headingMatch[2])
      const headingText = explicitIdMatch
        ? headingMatch[2].slice(0, explicitIdMatch.index)
        : headingMatch[2]
      const baseId = explicitIdMatch ? explicitIdMatch[1] : slugifyHeading(headingText)
      let headingId = baseId
      let suffix = 2
      while (headingIds.has(headingId)) headingId = baseId + '-' + suffix++
      headingIds.add(headingId)
      blocks.push(<Tag id={headingId} key={blocks.length} className={'docs-heading docs-h' + level}>{renderInline(headingText, 'h' + blocks.length)}</Tag>)
      i++
      continue
    }

    // Blockquote.
    if (/^>\s?/.test(line)) {
      const quoteLines = []
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        quoteLines.push(lines[i].replace(/^>\s?/, ''))
        i++
      }
      const isWarning = quoteLines[0].startsWith('⚠️')
      blocks.push(
        <blockquote className={'docs-blockquote' + (isWarning ? ' docs-blockquote-warning' : '')} key={blocks.length}>
          {quoteLines.map((quoteLine, quoteIndex) => <p key={quoteIndex}>{renderInline(quoteLine, 'bq' + blocks.length + '-' + quoteIndex)}</p>)}
        </blockquote>
      )
      continue
    }

    // Tables (pipe-delimited, with a header separator row).
    if (line.trim().startsWith('|') && lines[i + 1] && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1])) {
      const headerCells = line.trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim())
      i += 2
      const rows = []
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        rows.push(lines[i].trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim()))
        i++
      }
      blocks.push(
        <table className="docs-table" key={blocks.length}>
          <thead><tr>{headerCells.map((cell, cellIndex) => <th key={cellIndex}>{renderInline(cell, 'th' + blocks.length + '-' + cellIndex)}</th>)}</tr></thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}>{renderInline(cell, 'td' + blocks.length + '-' + rowIndex + '-' + cellIndex)}</td>)}</tr>
            ))}
          </tbody>
        </table>
      )
      continue
    }

    // Lists (unordered - / *, or ordered 1.). Wrapped continuation lines (no marker,
    // not blank, not a new list item) are appended to the previous item.
    const isUnordered = /^\s*[-*]\s+/.test(line)
    const isOrdered = /^\s*\d+\.\s+/.test(line)
    if (isUnordered || isOrdered) {
      const markerRegex = isUnordered ? /^\s*[-*]\s+/ : /^\s*\d+\.\s+/
      const items = []
      while (i < lines.length && markerRegex.test(lines[i])) {
        let item = lines[i].replace(markerRegex, '')
        i++
        while (i < lines.length && lines[i].trim() !== '' && !markerRegex.test(lines[i]) && !/^```/.test(lines[i])) {
          item += ' ' + lines[i].trim()
          i++
        }
        items.push(item)
      }
      const ListTag = isUnordered ? 'ul' : 'ol'
      blocks.push(
        <ListTag className="docs-list" key={blocks.length}>
          {items.map((item, itemIndex) => <li key={itemIndex}>{renderInline(item, 'li' + blocks.length + '-' + itemIndex)}</li>)}
        </ListTag>
      )
      continue
    }

    // Paragraph: gather contiguous non-blank plain lines.
    const paraLines = []
    while (i < lines.length && lines[i].trim() !== '' && !/^```/.test(lines[i]) && !/^#{1,4}\s/.test(lines[i])) {
      paraLines.push(lines[i])
      i++
    }
    blocks.push(<p className="docs-paragraph" key={blocks.length}>{renderInline(paraLines.join(' '), 'p' + blocks.length)}</p>)
  }

  return <div className="docs-markdown">{blocks}</div>
}



// WEBPACK FOOTER //
// ./src/components/docs/api/Markdown.js